import { enqueueSnackbar, closeSnackbar, SnackbarKey } from "notistack";
import { translateError } from "../utils/errorTranslation";

export type NotificationType = "success" | "error" | "warning" | "info";

interface NotificationConfig {
	showSuccess?: boolean;
	showError?: boolean;
	successMessage?: string;
	errorMessage?: string;
	loadingMessage?: string;
}

// Store for active loading notifications
let activeLoadingKeys: Map<string, SnackbarKey> = new Map();

/**
 * Show a success notification
 */
export function showSuccess(message: string): SnackbarKey {
	return enqueueSnackbar(message, {
		variant: "success",
		preventDuplicate: true,
		autoHideDuration: 2000,
	});
}

/**
 * Show an error notification
 */
export function showError(message: string): SnackbarKey {
	// Translate error message to German if needed
	const translatedMessage = translateError(message);
	return enqueueSnackbar(translatedMessage, {
		variant: "error",
		preventDuplicate: true,
		autoHideDuration: 3000,
	});
}

/**
 * Show a warning notification
 */
export function showWarning(message: string): SnackbarKey {
	return enqueueSnackbar(message, {
		variant: "warning",
		preventDuplicate: true,
		autoHideDuration: 2000,
	});
}

/**
 * Show an info notification
 */
export function showInfo(message: string): SnackbarKey {
	return enqueueSnackbar(message, {
		variant: "info",
		preventDuplicate: true,
		autoHideDuration: 2000,
	});
}

/**
 * Hide a loading notification
 */
export function hideLoading(keyOrSnackbarKey?: string | SnackbarKey): void {
	if (!keyOrSnackbarKey) {
		// Hide all loading notifications
		activeLoadingKeys.forEach((snackbarKey) => closeSnackbar(snackbarKey));
		activeLoadingKeys.clear();
	} else if (typeof keyOrSnackbarKey === "string") {
		// Hide by custom key
		const snackbarKey = activeLoadingKeys.get(keyOrSnackbarKey);
		if (snackbarKey) {
			closeSnackbar(snackbarKey);
			activeLoadingKeys.delete(keyOrSnackbarKey);
		}
	} else {
		// Hide by snackbar key
		closeSnackbar(keyOrSnackbarKey);
	}
}

/**
 * Extract resource name from endpoint
 */
function extractResourceName(endpoint: string): string {
	// Remove query params
	const path = endpoint.split("?")[0];
	// Get last segment
	const segments = path.split("/").filter((s) => s);
	const lastSegment = segments[segments.length - 1];

	// Check if it's an ID (number)
	if (!isNaN(Number(lastSegment)) && segments.length > 1) {
		// Use the segment before the ID
		const resource = segments[segments.length - 2];
		return formatResourceName(resource);
	}

	return formatResourceName(lastSegment);
}

/**
 * Format resource name for display
 */
function formatResourceName(resource: string): string {
	// Remove trailing 's' for plural
	let name = resource.endsWith("s") ? resource.slice(0, -1) : resource;
	// Capitalize first letter
	name = name.charAt(0).toUpperCase() + name.slice(1);
	// Handle special cases
	const specialCases: Record<string, string> = {
		Extra: "Profile",
		Extras: "Profile",
	};
	return specialCases[name] || name;
}

/**
 * Generate appropriate success message based on method and endpoint
 */
export function getSuccessMessage(method: string, endpoint: string): string {
	const resource = extractResourceName(endpoint);

	switch (method) {
		case "POST":
			return `${resource} created successfully!`;
		case "PUT":
			return `${resource} updated successfully!`;
		case "PATCH":
			return `${resource} changes saved successfully!`;
		case "DELETE":
			return `${resource} deleted successfully!`;
		default:
			return "Operation completed successfully!";
	}
}

/**
 * Generate appropriate error message based on method and endpoint
 */
export function getErrorMessage(
	method: string,
	endpoint: string,
	error?: string
): string {
	const resource = extractResourceName(endpoint);

	// If we have a specific error message, use it
	if (error) {
		return error;
	}

	// Otherwise generate based on method
	switch (method) {
		case "POST":
			return `Failed to create ${resource}`;
		case "PUT":
			return `Failed to update ${resource}`;
		case "PATCH":
			return `Failed to save ${resource} changes`;
		case "DELETE":
			return `Failed to delete ${resource}`;
		case "GET":
			return `Failed to load ${resource}`;
		default:
			return "Operation failed. Please try again.";
	}
}

/**
 * Handle API mutation notifications automatically
 * Used for POST, PUT, PATCH, DELETE operations
 */
export function handleMutationNotification(
	method: "POST" | "PUT" | "PATCH" | "DELETE",
	endpoint: string,
	config?: NotificationConfig
): {
	onSuccess: (response?: any) => void;
	onError: (error: string) => void;
} {
	const defaultConfig: NotificationConfig = {
		showSuccess: true,
		showError: true,
		successMessage: getSuccessMessage(method, endpoint),
		errorMessage: "Operation failed. Please try again.",
	};

	const finalConfig = { ...defaultConfig, ...config };
	let loadingKey: SnackbarKey | undefined;

	return {
		onSuccess: (response?: any) => {
			if (loadingKey) {
				hideLoading(loadingKey);
			}
			if (finalConfig.showSuccess) {
				// Use backend message if available, otherwise fall back to generated message
				const message = response?.data?.message || response?.message || finalConfig.successMessage!;
				showSuccess(message);
			}
		},
		onError: (error: string) => {
			if (loadingKey) {
				hideLoading(loadingKey);
			}
			if (finalConfig.showError) {
				// Error will be translated in showError function
				showError(error || finalConfig.errorMessage!);
			}
		},
	};
}

/**
 * Handle API error notifications
 * Shows error notification for any failed request
 */
export function handleErrorNotification(
	method: string,
	endpoint: string,
	error: string | Error | any,
	config?: { showError?: boolean; errorMessage?: string }
): void {
	const shouldShow = config?.showError !== false; // Default to true

	if (!shouldShow) return;

	let errorMessage: string;

	// If error is an object with error and message properties (backend format)
	if (error && typeof error === "object" && "error" in error) {
		// Use backend's error message directly
		errorMessage =
			error.message || error.error || getErrorMessage(method, endpoint);
	} else if (config?.errorMessage) {
		errorMessage = config.errorMessage;
	} else if (typeof error === "string") {
		errorMessage = error;
	} else if (error instanceof Error) {
		errorMessage = error.message;
	} else {
		errorMessage = getErrorMessage(method, endpoint);
	}

	// Error will be translated in showError function
	showError(errorMessage);
}
